'use client';

import './form-email-subscribe.scss';

import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.js';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.js';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { trackLeadFormSuccess } from '@sola/cookies/components/datalayer.js';
import { setSubscriptionCookie } from '@sola/skippy-sdk/cookies.js';

interface EmailFormData {
  firstName: string;
  lastName: string;
  email: string;
  subscriptionCode: string;
  isFirstNameRequired?: boolean;
  isLastNameRequired?: boolean;
  subscribeDirectionForm?: 'row' | 'column';
  showInputsPlaceholder?: boolean;
  titleSize?: 'xs' | 'sm' | 'md' | 'lg';
}

export type FormEmailSubscribeProps = {
  className?: string;
  description?: string;
  isFirstNameRequired?: boolean;
  isLastNameRequired?: boolean;
  onSubmit: (data: EmailFormData) => Promise<boolean>;
  showInputsPlaceholder?: boolean;
  subscribeDirectionForm?: 'row' | 'column';
  subscriptionCode: string;
  title?: string;
  titleSize?: 'xs' | 'sm' | 'md' | 'lg';
};

const FormEmailSubscribeState = z.object({
  showForm: z.boolean().optional(),
  showThankYou: z.boolean().optional(),
  isSubmitting: z.boolean().optional(),
  errorMessage: z.string().optional(),
});
type FormEmailSubscribeState = z.infer<typeof FormEmailSubscribeState>;

export const FormEmailSubscribe: React.FC<FormEmailSubscribeProps> = ({
  subscriptionCode,
  title,
  description,
  className,
  onSubmit,
  isFirstNameRequired = true,
  isLastNameRequired = true,
  subscribeDirectionForm = 'row',
  showInputsPlaceholder = false,
  titleSize = 'md',
}) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<FormEmailSubscribeState>({});
  const labels = {
    firstName: t('skippy.form.contactFields.firstNameLabel'),
    lastName: t('skippy.form.contactFields.lastNameLabel'),
    email: t('skippy.form.contactFields.emailLabel'),
  };

  const handleSubmit = async (data: FieldValues) => {
    const { firstName, lastName, email } = data;
    let errorMessage = '';

    setFormState((prevState) => ({
      ...prevState,
      errorMessage,
      isSubmitting: true,
    }));

    try {
      if (
        await onSubmit({
          firstName,
          lastName,
          email,
          subscriptionCode,
          isFirstNameRequired,
          isLastNameRequired,
        })
      ) {
        setSubscriptionCookie(subscriptionCode);
        trackLeadFormSuccess(subscriptionCode);
        setFormState({
          showForm: true,
          showThankYou: true,
          isSubmitting: false,
        });

        return;
      }

      errorMessage = t('skippy.form.errorVerify');
    } catch {
      errorMessage = t('skippy.form.errorSave');
    }

    if (errorMessage) {
      setFormState((prevState) => ({
        ...prevState,
        isSubmitting: false,
        errorMessage,
      }));
    }
  };

  useEffect(() => {
    setFormState((prevState) => ({
      ...prevState,
      showForm: true,
    }));
  }, [subscriptionCode]);

  const titleSizeClass = {
    xs: 'heading-xs',
    sm: 'heading-sm',
    md: 'heading-md',
    lg: 'heading-lg',
  };

  return (
    <div
      className={cn('EmailSubscribe component', {
        [`${className}`]: !!className,
      })}
    >
      {title && (
        <span className={`block ${titleSizeClass[titleSize]} mb-comfy-md`}>
          {title}
        </span>
      )}
      {description && (
        <span className="block label-lg mb-comfy-md">{description}</span>
      )}
      {formState.showThankYou ? (
        <Alert
          variant="positive"
          message={t('skippy.form.emailSubscribe.thankYou')}
        />
      ) : (
        <>
          {formState.errorMessage && (
            <div className="mb-comfy-xs">
              <Alert variant="negative" message={formState.errorMessage} />
            </div>
          )}

          <Form
            className={cn(
              'EmailSubscribe__form grid gap-comfy-lg md:gap-comfy-sm',
              {
                'grid-cols-1 row': subscribeDirectionForm === 'row',
                'grid-cols-1 column': subscribeDirectionForm === 'column',
              },
            )}
            onSubmit={handleSubmit}
            options={{ mode: 'onTouched' }}
            aria-label={t('skippy.form.emailSubscribe.ariaLabel')}
          >
            <EmailField
              key="email"
              label={labels.email}
              name="email"
              required
              placeholder={showInputsPlaceholder ? 'Email Address' : undefined}
            />
            <InputField
              key="firstName"
              label={labels.firstName}
              name="firstName"
              autoComplete="given-name"
              required={isFirstNameRequired}
              placeholder={showInputsPlaceholder ? 'First Name' : undefined}
            />
            <InputField
              key="lastName"
              label={labels.lastName}
              name="lastName"
              autoComplete="family-name"
              required={isLastNameRequired}
              placeholder={showInputsPlaceholder ? 'Last Name' : undefined}
            />
            <FormSubmit
              key="submit"
              className="EmailSubscribe__button mt-comfy-lg"
              isBusy={formState.isSubmitting}
            >
              {t('skippy.form.emailSubscribe.subscribe')}
            </FormSubmit>
          </Form>
        </>
      )}
    </div>
  );
};
